import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { AgFns, GridOptionsExt, GuidelineList, GuidelineListEditor, ISortModel, ProxAgFns, UtilDialogService } from '@core';
import { ActiveStatusEnum, Guideline, GuidelineContextEnum, GuidelineProgram, GuidelineAccount } from '@models';
import { ToastrService } from 'ngx-toastr';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';

/**
 * Guidelines tab of the Program form - GlobalAdmins only, like the rules themselves.
 *
 * Shows EVERY Claude rule that applies to this program, at every scope, so the admin sees exactly
 * what the review will use. A rule for this program (what the customer agreed, e.g. "inseams only
 * up to 34") is added and edited here. A broader rule is read-only here and is edited on
 * Utilities > Guidelines; this tab can still replace it for this program with an Override.
 */
@Component({
  selector: 'prox-program-guidelines-sub',
  templateUrl: './program-guidelines.sub.component.html',
  standalone: false,
})
export class ProgramGuidelinesSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  guidelines: Guideline[] = [];
  gridOptions: GridOptionsExt = {};
  readonly statusChoices = [
    { id: ActiveStatusEnum.Active, name: 'Active' },
    { id: ActiveStatusEnum.Inactive, name: 'Inactive' },
  ];
  private editor: GuidelineListEditor;
  private overrideList: GuidelineList;
  private isLoaded = false;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public toastr: ToastrService,
  ) {
    this.editor = new GuidelineListEditor(this.dbSaveService);
    this.overrideList = GuidelineListEditor.overrides(() => this.guidelines);
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params) => params.data.id,
    });
  }

  get program() {
    return this.parent.program;
  }

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);
    if (this.isLoaded && !forceRefresh) return;
    if (this.parent.isBeingAdded) {
      // A new program (or a new cycle) has no saved rules yet; they show after the first save.
      this.guidelines = [];
      this.isLoaded = false;
      AgFns.refreshGrid(this.gridOptions, this.guidelines);
      return;
    }
    await this.parent.wrapBusy(async () => {
      this.guidelines = await this.dbQueryService.getGuidelinesForProgram(this.program.id);
      this.isLoaded = true;
      AgFns.refreshGrid(this.gridOptions, this.guidelines);
    });
  }

  onGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** A rule scoped to programs is this program's own; a broader rule is edited on Utilities > Guidelines. */
  isOwn(g: Guideline) {
    return !!g && !g.isAllPrograms;
  }

  getColDefsAndSortModel() {
    const editable = (g: Guideline) => !this.isReadOnly() && this.isOwn(g);
    const colDefs: ColDef[] = [
      {
        headerName: 'Scope',
        colId: 'scope',
        valueGetter: (params) => this.scopeText(params.data),
        tooltipValueGetter: (params) => this.scopeText(params.data),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'In Effect',
        colId: 'inEffect',
        valueGetter: (params) => this.inEffectText(params.data),
        tooltipValueGetter: (params) => this.inEffectText(params.data),
        ...AgFns.flexWidth(160),
      },
      {
        headerName: 'Title',
        field: 'title',
        editable: (params) => editable(params.data),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Instruction',
        field: 'instruction',
        editable: (params) => editable(params.data),
        filter: 'agTextColumnFilter',
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 8000, rows: 12, cols: 80 },
        tooltipField: 'instruction',
        ...AgFns.flexWidth(460),
      },
      this.editor.colDef('Overrides', this.overrideList, editable, 200),
      {
        headerName: 'Status',
        ...AgFns.createRichDropdownEditor('activeStatusId', this.statusChoices),
        editable: (params) => editable(params.data),
        ...AgFns.fixWidth(100),
      },
      ProxAgFns.getEntityDeleteColDef(this.onDelete.bind(this)),
    ];
    const sortModel: ISortModel = [{ colId: 'scope', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  /** Where a rule applies, from the narrowest scope it has. */
  scopeText(g: Guideline): string {
    if (!g) return '';
    const parts: string[] = [];
    if (!g.isAllPrograms) {
      const others = g.guidelinePrograms.filter((p) => p.programId !== this.program.id).length;
      parts.push(others ? `This program + ${others} more` : 'This program');
    }
    if (!g.isAllAccounts) {
      parts.push(g.guidelineAccounts.length === 1 ? 'This account' : `${g.guidelineAccounts.length} accounts`);
    }
    if (!g.isAllSuppliers) {
      parts.push('Suppliers: ' + g.guidelineSuppliers.map((s) => s.supplier?.name ?? '?').join(', '));
    }
    return parts.length ? parts.join('; ') : 'All';
  }

  /** Whether the review will use the rule for this program, and if not, why. */
  inEffectText(g: Guideline): string {
    if (!g) return '';
    if (g.activeStatusId === ActiveStatusEnum.Inactive) return 'No - Inactive';
    const by = this.guidelines.filter(
      (r) =>
        r !== g &&
        r.activeStatusId !== ActiveStatusEnum.Inactive &&
        r.guidelineOverrides.some((o) => o.overriddenGuidelineId === g.id),
    );
    return by.length ? 'No - replaced by ' + by.map((r) => GuidelineListEditor.ruleName(r)).join(', ') : 'Yes';
  }

  /** A new rule for THIS program only. */
  onAdd() {
    const g = this.dbSaveService.createEntity(Guideline, {
      guidelineContextId: GuidelineContextEnum.ProgramReview,
      isAllAccounts: false,
      isAllSuppliers: true,
      isAllPrograms: false,
      instruction: '',
      activeStatusId: ActiveStatusEnum.Active,
    });
    this.dbSaveService.createEntity(GuidelineProgram, { guidelineId: g.id, programId: this.program.id });
    this.dbSaveService.createEntity(GuidelineAccount, { guidelineId: g.id, accountId: this.program.accountId });
    this.guidelines = [...this.guidelines, g];
    AgFns.refreshGrid(this.gridOptions, this.guidelines);
  }

  /** Only an unsaved row is removed. A saved guideline may have findings, so it is set Inactive instead. */
  onDelete(g: Guideline) {
    if (!g.entityAspect.entityState.isAdded()) {
      this.toastr.warning('A saved guideline is never deleted. Set its Status to Inactive.', 'Guidelines');
      return;
    }
    this.editor.detachRows(g);
    g.entityAspect.setDetached();
    this.guidelines = this.guidelines.filter((x) => x !== g);
    AgFns.refreshGrid(this.gridOptions, this.guidelines);
  }

  /** Called by the Program form before it saves. False = do not save. */
  async beforeSave(): Promise<boolean> {
    const problems: string[] = [];
    for (const g of this.guidelines.filter((x) => !x.entityAspect.entityState.isUnchanged())) {
      g.instruction = (g.instruction ?? '').trim();
      g.title = g.title?.trim() || undefined;
      problems.push(...this.editor.problems(g));
    }
    if (problems.length > 0) {
      await this.dialogService.okDialog('Cannot save the Guidelines', [...new Set(problems)].join('<br/>'));
      return false;
    }
    return true;
  }

  isReadOnly() {
    return this.parent.isReadOnly() || !this.parent.authUser?.isGlobalAdmin;
  }
}
